import { useState, useCallback, useEffect, useMemo, memo, useContext } from 'react';
import { areControllerPropsEqual } from '../utilities/utils';
import { CropSquareOutlined, HorizontalRule } from '@mui/icons-material';
import { ControllerContext } from '../ts/Contexts';
import { IDownloadLogEntry } from '../utilities/database';
import debounce from 'lodash/debounce';

interface DownloadTotal {
    path: string;
    calls: number;
    kb: number;
}

// Collapses per-user and per-day segments so the same read across many users aggregates into one row.
const normalisePath = (caller: string): string => {
    const firstSpace = caller.indexOf(' ');
    const rawPath = firstSpace === -1 ? caller : caller.slice(0, firstSpace);
    const suffix = firstSpace === -1 ? '' : caller.slice(firstSpace + 1);

    let path = rawPath;
    if (path.startsWith('https:')) path = `/${path.split('/').slice(3).join('/')}`;

    const segments = path.split('/').map((segment) => {
        const bare = segment.replace('-ownerProfile', '');
        if (!bare) return segment;
        if (/^\d+$/.test(bare)) return '*';
        if (/^[A-Za-z0-9_-]{16,}$/.test(bare)) return '*';
        return segment;
    });

    return suffix ? `${segments.join('/')} ${suffix}` : segments.join('/');
};

// roughSizeOfObject reports decoded JS size in kb, so treat these as relative, not wire-exact.
const formatMb = (kb: number): string => `${(kb / 1024).toFixed(2)} MB`;

const MemoryProfiler = (): JSX.Element => {
    const controller = useContext(ControllerContext);
    const [entries, setEntries] = useState<IDownloadLogEntry[]>([]);
    const [minimised, setMinimised] = useState<boolean>(true);

    const database = controller.getDatabase();

    const refresh = useCallback(() => {
        setEntries([...database.getDownloadLog()]);
    }, [database]);

    useEffect(() => {
        const scheduleRefresh = debounce(refresh, 500);
        database.addLogSizeCallback(scheduleRefresh);
        refresh();
        return () => {
            scheduleRefresh.cancel();
            database.removeLogSizeCallback(scheduleRefresh);
        };
    }, [database, refresh]);

    const rows: DownloadTotal[] = useMemo(() => {
        const totals: Record<string, DownloadTotal> = {};
        entries.forEach(({ caller, size }) => {
            const path = normalisePath(caller);
            if (!totals[path]) totals[path] = { path, calls: 0, kb: 0 };
            totals[path].calls += 1;
            totals[path].kb += size;
        });
        return Object.values(totals).sort((a, b) => b.kb - a.kb);
    }, [entries]);

    const totalKb = useMemo(() => rows.reduce((sum, row) => sum + row.kb, 0), [rows]);

    return (
        <div id="memoryProfiler" style={{ height: minimised ? '30px' : '350px', left: 0 }}>
            <div id="memoryProfilerheader">
                {`Downloads ${formatMb(totalKb)}`}
                {!minimised && <span style={{ position: 'absolute', right: 0 }}><HorizontalRule onClick={() => setMinimised(true)} /></span>}
                {minimised && <span style={{ position: 'absolute', right: 0 }}><CropSquareOutlined onClick={() => setMinimised(false)} /></span>}
            </div>
            {!minimised && (
                <div style={{ height: 315, overflowY: 'auto', fontSize: 11 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left' }}>Path</th>
                                <th style={{ textAlign: 'right' }}>Calls</th>
                                <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.path}>
                                    <td style={{ wordBreak: 'break-all' }} title={row.path}>{row.path}</td>
                                    <td style={{ textAlign: 'right' }}>{row.calls}</td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMb(row.kb)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default memo(MemoryProfiler, areControllerPropsEqual);
